import React, { ChangeEvent, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import WhiteSquare from '../../../WhiteSquare';
import {
  FileUploadContainer,
  ModalLabel as ModalLabelBase,
  ErrorLabel,
  HiddenInput
} from '../../lib/styled';
import SubmitOrCancel from '../../components/SubmitOrCancel';
import { themes } from 'kb-shared';

const buttonSize = 145;

export const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ModalLabel = styled(ModalLabelBase)`
  max-width: 200px;
`;

const imageSize = 'height:240px;width:320px;';

const UploadImageContainer = styled.div`
  align-items: center;
  display: flex;
  padding: 15px 0 0 0;
  justify-content: center;
  ${imageSize}
`;

const Image = styled.img`
  object-fit: contain;
  ${imageSize}
`;

const Description = styled.small`
  ${themes.fonts.FoundersGrotesk};
  max-width: 140px;
  display: inline-block;
  text-align: center;
  margin: 8px 0 0 0;
`;

interface SafeRenderImageProps {
  src: string;
  type?: string | null;
}

const SafeRenderImage = ({ src, type }: SafeRenderImageProps) => {
  if (!type?.startsWith('image/'))
    return <Description>File selected. Preview is not available for this file type.</Description>;

  return <Image src={src} />;
};

type Props = {
  label: string;
  image?: string | null;
  type?: string | null;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  onCancel: () => void;
  loading: boolean;
  error?: string | null;
  loadingPercentage: number | null;
};

const InsuranceCardUpload = (props: Props) => {
  const {
    label,
    error,
    image,
    type,
    onFileChange,
    onSubmit,
    onCancel,
    loading,
    loadingPercentage
  } = props;
  const { t } = useTranslation();

  const fileUploadRef = useRef<HTMLInputElement | null>(null);

  return (
    <Container>
      <ModalLabel>{label}</ModalLabel>
      {error && <ErrorLabel>{error}</ErrorLabel>}
      <UploadImageContainer>
        {image ? (
          <SafeRenderImage src={image} type={type} />
        ) : (
          <>
            <HiddenInput
              type="file"
              onChange={onFileChange}
              accept=".jpeg, .jpg, .png"
              ref={fileUploadRef}
            />
            <FileUploadContainer size={buttonSize} onClick={() => fileUploadRef.current?.click()}>
              <WhiteSquare />
              <Description>{t('upload_button.help_text', { megabytes: 2 })}</Description>
            </FileUploadContainer>
          </>
        )}
      </UploadImageContainer>
      <SubmitOrCancel
        onSubmit={onSubmit}
        onCancel={onCancel}
        showSubmit={!!image}
        loading={loading}
        loadingPercentage={loadingPercentage}
      />
    </Container>
  );
};

export default InsuranceCardUpload;
